/**
 * Prévisualisation des macros d'une recette — **jamais une source de vérité**.
 *
 * ⚠️ Même statut que `lib/foodPreview.ts`, et pour la même raison (§5.1) : ce que
 * ce fichier produit n'est **jamais envoyé** au serveur. `RecipeInput` n'a aucun
 * champ de macro et le corps est en `strict` — un total envoyé serait refusé en
 * 400, pas ignoré. Ces fonctions ne servent qu'à deux choses :
 *
 *   - montrer le total pendant qu'on règle les ingrédients dans l'éditeur, pour
 *     que le §6.5 (« macros calculées automatiquement ») se voie à la frappe et
 *     pas seulement après enregistrement ;
 *   - alimenter la mise à jour optimiste du bandeau quand on ajoute une recette
 *     au journal (§6.2), écrasée par la réponse du serveur dès qu'elle arrive.
 *
 * ⚠️ Ces calculs dupliquent `resolveRecipeIngredients()` et `resolveRecipe()`
 * côté serveur, exactement comme `foodPreview.ts` duplique `resolveFood()`. Même
 * arbitrage, même conséquence en cas de divergence : le serveur gagne, le temps
 * d'un aller-retour.
 */

import type { MacroSet, RecipeDTO } from '../../../shared/types';
import { previewMacrosFor } from './foodPreview';

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

const NO_MACROS: MacroSet = { protein: 0, carbs: 0, fat: 0, kcal: 0 };

export function sumMacros(list: readonly MacroSet[]): MacroSet {
  const total = list.reduce<MacroSet>(
    (accumulator, macros) => ({
      protein: accumulator.protein + macros.protein,
      carbs: accumulator.carbs + macros.carbs,
      fat: accumulator.fat + macros.fat,
      kcal: accumulator.kcal + macros.kcal,
    }),
    NO_MACROS,
  );
  return {
    protein: round2(total.protein),
    carbs: round2(total.carbs),
    fat: round2(total.fat),
    kcal: round2(total.kcal),
  };
}

/** Ramène un total à une portion, ou à 100 g de plat fini. */
export function divideMacros(macros: MacroSet, divisor: number, multiplier = 1): MacroSet {
  if (divisor <= 0) return NO_MACROS;
  const factor = multiplier / divisor;
  return {
    protein: round2(macros.protein * factor),
    carbs: round2(macros.carbs * factor),
    fat: round2(macros.fat * factor),
    kcal: round2(macros.kcal * factor),
  };
}

/**
 * Une ligne du bloc, telle qu'elle sera écrite au journal.
 *
 * `quantity` est **toujours** en unité de base : c'est ce que le serveur fige
 * dans `log_entry_ingredient`, et c'est donc ce que l'écran d'ajustement doit
 * montrer et laisser corriger.
 */
export interface PreviewLine {
  /** `recipe_ingredient.id` — ce que l'ajustement renvoie au serveur. */
  ingredientId: number;
  label: string;
  quantity: number;
  unit: string;
  macros: MacroSet;
}

/**
 * Échelle appliquée par défaut aux quantités d'ingrédients.
 *
 * `quantity / yieldValue`, quel que soit le type de rendement : une portion sur
 * quatre et 300 g sur 1 200 g donnent tous deux 0,25. Miroir exact de
 * `resolveRecipe()`.
 */
export function scaleOf(recipe: RecipeDTO, quantity: number): number {
  return recipe.yieldValue > 0 ? Math.max(0, quantity) / recipe.yieldValue : 0;
}

/**
 * Lignes proposées pour une quantité consommée — le point de départ de l'écran
 * d'ajustement (specs §6.5-2), avant toute correction manuelle.
 */
export function previewLinesFor(recipe: RecipeDTO, quantity: number): PreviewLine[] {
  const scale = scaleOf(recipe, quantity);
  return recipe.ingredients.map((item) => {
    const baseQuantity = round2(item.baseQuantity * scale);
    return {
      ingredientId: item.id,
      label: item.label,
      quantity: baseQuantity,
      unit: item.baseUnit,
      // Les macros de l'ingrédient sont connues pour `baseQuantity` : on
      // redivise par 100 comme le serveur, à partir de la fiche implicite.
      macros: macrosOfLine(item.macros, item.baseQuantity, baseQuantity),
    };
  });
}

/**
 * Macros d'une ligne pour une nouvelle quantité, par règle de trois sur les
 * macros déjà calculées pour la quantité de référence.
 *
 * Le modèle est linéaire (`per100 × qty / 100`), donc ce raccourci donne le même
 * résultat que le serveur sans avoir à transporter `per100` pour chaque
 * ingrédient. `from` nul → rien à mettre à l'échelle.
 */
export function macrosOfLine(macros: MacroSet, from: number, to: number): MacroSet {
  if (from <= 0) return NO_MACROS;
  return previewMacrosFor(
    {
      protein: (macros.protein * 100) / from,
      carbs: (macros.carbs * 100) / from,
      fat: (macros.fat * 100) / from,
      kcal: (macros.kcal * 100) / from,
    },
    to,
  );
}

/**
 * Libellé figé d'une entrée de recette, tel que le serveur l'écrira.
 *
 * Reproduit `composeLabel(row.name, null)` de `services/macros.ts` : si l'aperçu
 * affichait autre chose, la ligne changerait de texte sous les yeux de
 * l'utilisateur au moment où le serveur répond.
 */
export function composeRecipeLabel(name: string): string {
  return name.length <= 100 ? name : name.slice(0, 100);
}

/** Libellé de portion figé : « 1 sur 4 portions ». `null` pour un rendement en poids. */
export function composeRecipePortionLabel(recipe: RecipeDTO, quantity: number): string | null {
  if (recipe.yieldType !== 'portions') return null;
  return `${String(round2(Math.max(0, quantity)))} sur ${String(round2(recipe.yieldValue))} portions`;
}
